import i18next, { Callback, TFunction } from "i18next"
import { InternationalizedText } from "../PlaceDataUtils"
import InputSanitizationUtils from "../InputSanitizationUtils"

export type LanguageDetail = {
    languageCode: string
    countryCode: string
    languageName: string
    langugageIcon?: string
}

export default class I18nTextUtils {

    private static logLocale = () => {

        // Curent detected or language
        // If non suported language is selected e.g. fr if not supported, this shows it. Even if nonsense string is passed there
        console.info("I18nTextUtils language: ", i18next.language)

        // Curent resolved language
        // If language is set to some unknown e.g. fr if not supported, this returns default lang e.g. en
        // If czech is recognized as cs-CZ, 
        console.info("I18nTextUtils resolvedLanguage: ", i18next.resolvedLanguage)

        console.info("I18nTextUtils languages: ", i18next.languages)

        console.info("I18nTextUtils languageDetailList: ", this.getLanguageDetailList())
    }

    public static getCurrentLanguage = (): string => {
        return i18next.resolvedLanguage != null ? i18next.resolvedLanguage : this.getLanguageDetailList()[this.DEFAULT_LANGUAGE_DETAIL_ARRAY].languageCode
    }

    public static getCurrentLanguageDetail = (): LanguageDetail => {
        const currentLanguageDetail = this.getLanguageDetailList().find(l => l.languageCode == this.getCurrentLanguage())

        return currentLanguageDetail ? currentLanguageDetail : this.getLanguageDetailList()[this.DEFAULT_LANGUAGE_DETAIL_ARRAY]
    }

    /**
     * Resolves internationalized text and returns sanitized plain text.
     * This method automatically sanitizes the output to prevent XSS attacks.
     * 
     * @param texts - Array of internationalized text objects
     * @returns Sanitized plain text string
     */
    public static resolveInternationalizedText = (texts: InternationalizedText[] | undefined): string => {

        if (!texts || !texts.length) {
            return ""
        }

        const foundText: InternationalizedText | undefined = texts.find(t => t.locale == I18nTextUtils.getCurrentLanguage())

        const defaultLangDetail: LanguageDetail = this.getLanguageDetailList()[this.DEFAULT_LANGUAGE_DETAIL_ARRAY]
        const defaultLangText: InternationalizedText | undefined = texts.find(t => t.locale == defaultLangDetail.languageCode)

        let result: string;
        if (foundText) {
            result = foundText.content // + " [" + foundText.locale + "]"
        } else if (defaultLangText) {
            // Check if it is possible to default to default lang
            result = defaultLangText.content // + " [" + defaultLangText.locale + "]"
        } else {
            // Use any other available lang
            result = texts[0]?.content // + " [" + texts[0].locale + "]"
        }

        // Sanitize the output to prevent XSS attacks
        return InputSanitizationUtils.sanitizePlainText(result);
    }

    /**
     * Resolves internationalized text and returns sanitized HTML.
     * Use this method when displaying content with dangerouslySetInnerHTML.
     * 
     * @param texts - Array of internationalized text objects
     * @returns Sanitized HTML string
     */
    public static resolveInternationalizedTextAsHtml = (texts: InternationalizedText[] | undefined): string => {

        if (!texts || !texts.length) {
            return ""
        }

        const foundText: InternationalizedText | undefined = texts.find(t => t.locale == I18nTextUtils.getCurrentLanguage())

        const defaultLangDetail: LanguageDetail = this.getLanguageDetailList()[this.DEFAULT_LANGUAGE_DETAIL_ARRAY]
        const defaultLangText: InternationalizedText | undefined = texts.find(t => t.locale == defaultLangDetail.languageCode)

        let result: string;
        if (foundText) {
            result = foundText.content + " [" + foundText.locale + "]"
        } else if (defaultLangText) {
            // Check if it is possible to default to default lang
            result = defaultLangText.content + " [" + defaultLangText.locale + "]"
        } else {
            // Use any other available lang
            result = texts[0]?.content + " [" + texts[0].locale + "]"
        }

        // Sanitize HTML to allow safe formatting tags
        return InputSanitizationUtils.sanitizeRichText(result);
    }

    public static changeLanguage = (lng: string, callback?: Callback | undefined): Promise<TFunction> => {
        return i18next.changeLanguage(lng, callback);
    }

    private static getLocalizedStringFromI18n = (lng: string, jsonPath: string[]): string => {
        const resourceBundle = i18next.getResourceBundle(lng, "")

        if (!resourceBundle) {
            return "N/A";
        }

        var foundResult = resourceBundle

        jsonPath.forEach(s => {
            if (foundResult && typeof foundResult === 'object') {
                foundResult = foundResult[s]
            } else {
                foundResult = undefined;
            }
        })

        return foundResult ? foundResult : "N/A";
    }

    private static getLocalizedLanguageName = (lng: string): string => {
        const name = this.getLocalizedStringFromI18n(lng, ["localization", "language-name"]);
        // Fallback to hardcoded names if resource bundle not loaded yet
        if (name === "N/A") {
            const fallbackNames: Record<string, string> = {
                "en": "English",
                "cs": "Čeština",
                "es": "Español",
                "fr": "Français",
                "zh-CN": "简体中文",
                "ar": "العربية",
                "de": "Deutsch",
                "pt": "Português",
                "hi": "हिन्दी",
                "bn": "বাংলা",
                "ja": "日本語",
                "ko": "한국어",
                "pl": "Polski"
            };
            return fallbackNames[lng] || lng;
        }
        return name;
    }

    private static readonly DEFAULT_LANGUAGE_DETAIL_ARRAY: number = 0;

    static getLanguageDetailList(): LanguageDetail[] {
        const languageDetailList: LanguageDetail[] = [
            // Keep en on [0] used for default
            { languageCode: "en", countryCode: "GB", languageName: this.getLocalizedLanguageName("en") },

            { languageCode: "cs", countryCode: "CZ", languageName: this.getLocalizedLanguageName("cs") },
            { languageCode: "es", countryCode: "ES", languageName: this.getLocalizedLanguageName("es") },
            { languageCode: "fr", countryCode: "FR", languageName: this.getLocalizedLanguageName("fr") },
            { languageCode: "zh-CN", countryCode: "CN", languageName: this.getLocalizedLanguageName("zh-CN") },
            { languageCode: "ar", countryCode: "SA", languageName: this.getLocalizedLanguageName("ar") },
            { languageCode: "de", countryCode: "DE", languageName: this.getLocalizedLanguageName("de") },
            { languageCode: "pt", countryCode: "PT", languageName: this.getLocalizedLanguageName("pt") },
            { languageCode: "hi", countryCode: "IN", languageName: this.getLocalizedLanguageName("hi") },
            { languageCode: "bn", countryCode: "BD", languageName: this.getLocalizedLanguageName("bn") },
            { languageCode: "ja", countryCode: "JP", languageName: this.getLocalizedLanguageName("ja") },
            { languageCode: "ko", countryCode: "KR", languageName: this.getLocalizedLanguageName("ko") },
            { languageCode: "pl", countryCode: "PL", languageName: this.getLocalizedLanguageName("pl") },
        ]

        return languageDetailList
    }
}

// Keys to be used in InternationalizedTexts table
export class I18nTextKeys {

    // Keys relevant to places
    static Places = class {
        public static readonly PLACE_NAME: string = "place-name"
        public static readonly PLACE_DESCRIPTION: string = "place-description"
        public static readonly PLACE_RATING_COMMENT: string = "place-rating-comment"
    }

}
